import { QueryFunctionContext } from "react-query";
import onBoardingApi from "./onBoardingApi";

export type PipelineRunStatus = "QUEUED" | "RUNNING" | "COMPLETED" | "FAILED";

export interface CaptableRunSummary {
  id: string;
  cin: string;
  pas3Keys: string[];
  mgt7Key: string | null;
  status: PipelineRunStatus;
  phase: string | null;
  jobId: string;
  resultKey: string | null;
  resultUrl: string | null;
  urlExpiry: string | null;
  // Generated Stage-4 onboarding workbook (draft) uploaded next to the
  // stage-3 result. Null on older runs or when the stage-4 fill failed.
  stage4Key: string | null;
  stage4Url: string | null;
  stage4UrlExpiry: string | null;
  error: string | null;
  inputTokens: number | null;
  outputTokens: number | null;
  costUsd: number | null;
  extractionStats: CaptableExtractionStats | null;
  createdAt: string;
  updatedAt: string;
}

export interface FinancialFileStats {
  file: string;
  financialYear: string;
  method: string;
  hasBalanceSheet: boolean;
  hasProfitAndLoss: boolean;
  nullFields: number;
  error: string | null;
}

export interface FinancialExtractionStats {
  totalFiles: number;
  successfulFiles: number;
  failedFiles: number;
  files: FinancialFileStats[];
}

export interface FinancialRunSummary {
  id: string;
  cin: string;
  aoc4Keys: string[];
  status: PipelineRunStatus;
  phase: string | null;
  jobId: string;
  resultKey: string | null;
  resultUrl: string | null;
  urlExpiry: string | null;
  error: string | null;
  inputTokens: number | null;
  outputTokens: number | null;
  extractionStats: FinancialExtractionStats | null;
  captableUploaded: boolean;
  captableUploadedAt: string | null;
  captableCompanyId: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface Stage4Excel {
  id: string;
  cin: string;
  s3Key: string;
  filename: string;
  fileUrl: string;
  urlExpiry: string | null;
  uploadedBy: string;
  createdAt: string;
  updatedAt: string;
}

// A company that already exists in stage-3 (created outside this pipeline).
export interface Stage3CompanySummary {
  id: string;
  name: string;
  cin: string;
  stage: string;
}

export interface S3CompanyEntry {
  cin: string;
  name: string | null;
  isOnboarded: boolean;
  publishedCompanyId: string | null;
  latestCaptableRun: CaptableRunSummary | null;
  latestFinancialRun: FinancialRunSummary | null;
  stage4Excel: Stage4Excel | null;
  stage3Company: Stage3CompanySummary | null;
}

export interface ListS3FilesResponse {
  keys: S3CompanyEntry[];
}

export async function listS3Files(
  context: QueryFunctionContext,
): Promise<ListS3FilesResponse> {
  return onBoardingApi
    .get(`data-pipeline/list-s3-files`)
    .then((res) => res.data.data);
}

export interface EnqueueDownloadPayload {
  keys: string[];
  cin: string;
}

export interface EnqueueDownloadResponse {
  jobId: string;
  keyCount: number;
}

export async function enqueueDownload(
  payload: EnqueueDownloadPayload,
): Promise<EnqueueDownloadResponse> {
  return onBoardingApi
    .post(`data-pipeline/downloads`, payload)
    .then((res) => res.data.data);
}

export type DownloadJobState =
  | "waiting"
  | "active"
  | "completed"
  | "failed"
  | "delayed";

export interface DownloadJobReturnValue {
  bucket: string;
  key: string;
  fileUrl: string;
  fileSize: number;
  keyCount: number;
}

export interface DownloadJobStatus {
  jobId: string;
  state: DownloadJobState;
  progress: number;
  attemptsMade: number;
  returnvalue: DownloadJobReturnValue | null;
  failedReason: string | null;
}

export async function getDownloadJob(
  jobId: string,
): Promise<DownloadJobStatus> {
  return onBoardingApi
    .get(`data-pipeline/downloads/${jobId}`)
    .then((res) => res.data.data);
}

// A selected file paired with the filing date parsed from its filename, as
// YYYY-MM-DD (UTC) or null when no date could be extracted.
export interface CaptableFileRef {
  key: string;
  date: string | null;
}

export interface EnqueueCaptablePayload {
  cin: string;
  mgt7: CaptableFileRef;
  // One entry per selected PAS-3; the backend uses the dates to pick the latest.
  pas3: CaptableFileRef[];
}

export interface EnqueueCaptableData {
  runId: string;
  jobId: string;
  status: string;
  deduped?: boolean;
}

export async function enqueueCaptable(
  payload: EnqueueCaptablePayload,
): Promise<EnqueueCaptableData> {
  return onBoardingApi
    .post(`data-pipeline/captable`, payload)
    .then((res) => res.data.data);
}

// Server-side cap on items per batch enqueue request.
export const MAX_BATCH_CINS = 200;

export interface CaptableBatchItem {
  cin: string;
  mgt7: CaptableFileRef;
  pas3: CaptableFileRef[];
}

export interface EnqueueCaptableBatchPayload {
  items: CaptableBatchItem[];
}

// One entry per submitted item, in the same order. `deduped: true` means an
// identical run (same CIN + same keys) was already QUEUED/RUNNING and its
// runId is returned instead of a duplicate job. `ok: false` entries carry an
// error and don't block the other items.
export interface BatchEnqueueResult {
  cin: string;
  ok: boolean;
  runId?: string;
  jobId?: string;
  status?: string;
  deduped?: boolean;
  error?: string;
}

export interface BatchEnqueueData {
  total: number;
  queued: number;
  results: BatchEnqueueResult[];
}

export async function enqueueCaptableBatch(
  payload: EnqueueCaptableBatchPayload,
): Promise<BatchEnqueueData> {
  return onBoardingApi
    .post(`data-pipeline/captable/batch`, payload)
    .then((res) => res.data.data);
}

export type CaptableRunStatus = "QUEUED" | "RUNNING" | "COMPLETED" | "FAILED";

export interface Mgt7ExtractionStats {
  file: string;
  shareholders: number;
  providesIndividualList: boolean;
}

export interface Pas3ExtractionStats {
  file: string;
  rounds: number;
  allottees: number;
  placeholders: number;
  loas: number;
  loaIssues: string[];
}

export interface CaptableExtractionStats {
  mgt7: Mgt7ExtractionStats | null;
  pas3s: Pas3ExtractionStats[];
  stage3Rows: number;
  allotmentRows: number;
}

export interface CaptableRunData {
  runId: string;
  cin: string;
  status: CaptableRunStatus;
  phase: string | null;
  jobId: string;
  resultUrl: string | null;
  urlExpiry: string | null;
  stage4Url: string | null;
  stage4UrlExpiry: string | null;
  error: string | null;
  inputTokens: number | null;
  outputTokens: number | null;
  extractionStats: CaptableExtractionStats | null;
  createdAt: string;
  updatedAt: string;
}

export async function getCaptableRun(runId: string): Promise<CaptableRunData> {
  return onBoardingApi
    .get(`data-pipeline/captable/${runId}`)
    .then((res) => res.data.data);
}

export interface CancelCaptableData {
  runId: string;
  status: CaptableRunStatus;
  // Only present when this call actually performed the cancellation; absent
  // when the run had already finished (already COMPLETED/FAILED).
  cancelled?: boolean;
}

export async function cancelCaptableRun(
  runId: string,
): Promise<CancelCaptableData> {
  return onBoardingApi
    .post(`data-pipeline/captable/${runId}/cancel`, {})
    .then((res) => res.data.data);
}

// A run that was cancelled ends as FAILED, but its `error` is prefixed with
// "CANCELLED:". Use this to distinguish a user-cancelled run from a real failure.
export function isCaptableRunCancelled(error: string | null): boolean {
  return !!error && error.startsWith("CANCELLED");
}

export interface UploadCaptableExcelPayload {
  runId: string;
  cin: string;
  // The edited workbook, serialized client-side back to .xlsx
  file: Blob;
  fileName: string;
  // Which generated artifact to overwrite: the stage-3 result (default) or
  // the generated stage-4 onboarding workbook.
  target?: "stage3" | "stage4";
}

export interface UploadCaptableExcelData {
  runId: string;
  resultKey: string | null;
  resultUrl: string | null;
  urlExpiry?: string | null;
  fileSize?: number | null;
}

export interface RerunMgt7Payload {
  mgt7?: { key: string };
}

export interface CaptableRerunData {
  runId: string;
  jobId: string;
  status: string;
  mode?: string;
  sourceRunId?: string;
  deduped?: boolean;
}

export interface RerunMgt7Data extends CaptableRerunData {}

export interface RerunPas3Payload {
  pas3?: Array<{ key: string }>;
}

export interface RerunPas3Data extends CaptableRerunData {
  pas3KeysToRerun?: string[];
}

export async function rerunMgt7(
  sourceRunId: string,
  payload: RerunMgt7Payload = {},
): Promise<RerunMgt7Data> {
  return onBoardingApi
    .post(`data-pipeline/captable/${sourceRunId}/rerun-mgt7`, payload)
    .then((res) => res.data.data);
}

export async function rerunPas3(
  sourceRunId: string,
  payload: RerunPas3Payload = {},
): Promise<RerunPas3Data> {
  return onBoardingApi
    .post(`data-pipeline/captable/${sourceRunId}/rerun-pas3`, payload)
    .then((res) => res.data.data);
}

// Uploads a user-edited captable workbook back to the pipeline so it replaces
// the generated result for this run.
// NOTE: endpoint path + form field name are placeholders pending the real
// upload endpoint — adjust the URL/field below once it's confirmed.
export async function uploadCaptableExcel(
  payload: UploadCaptableExcelPayload,
): Promise<UploadCaptableExcelData> {
  const formData = new FormData();
  formData.append("file", payload.file, payload.fileName);
  formData.append("cin", payload.cin);
  if (payload.target) formData.append("target", payload.target);
  return onBoardingApi
    .post(`data-pipeline/captable/${payload.runId}/excel`, formData, {
      headers: { "Content-Type": "multipart/form-data" },
    })
    .then((res) => res.data.data);
}

export interface EnqueueFinancialPayload {
  cin: string;
  aoc4Keys: string[];
}

export interface EnqueueFinancialData {
  runId: string;
  jobId: string;
  status: string;
  deduped?: boolean;
}

export type FinancialRunStatus = "QUEUED" | "RUNNING" | "COMPLETED" | "FAILED";

export interface FinancialRunData {
  runId: string;
  cin: string;
  status: FinancialRunStatus;
  phase: string | null;
  resultUrl: string | null;
  urlExpiry: string | null;
  inputTokens: number | null;
  outputTokens: number | null;
}

export async function enqueueFinancial(
  payload: EnqueueFinancialPayload,
): Promise<EnqueueFinancialData> {
  return onBoardingApi
    .post(`data-pipeline/financial`, payload)
    .then((res) => res.data.data);
}

export interface FinancialBatchItem {
  cin: string;
  aoc4Keys: string[];
}

export interface EnqueueFinancialBatchPayload {
  items: FinancialBatchItem[];
}

export async function enqueueFinancialBatch(
  payload: EnqueueFinancialBatchPayload,
): Promise<BatchEnqueueData> {
  return onBoardingApi
    .post(`data-pipeline/financial/batch`, payload)
    .then((res) => res.data.data);
}

export async function getFinancialRun(
  runId: string,
): Promise<FinancialRunData> {
  return onBoardingApi
    .get(`data-pipeline/financial/${runId}`)
    .then((res) => res.data.data);
}

// Re-run an AOC-4 financial extraction with the high-quality method (GPT-5
// escalation + Azure OCR). Omit aoc4Keys to re-extract every AOC-4 from the
// source run, or pass a subset (each key must belong to the source run).
export interface RerunFinancialPayload {
  aoc4Keys?: string[];
}

export interface RerunFinancialData {
  runId: string;
  jobId: string | null;
  status: string;
  mode?: string;
  sourceRunId?: string;
  aoc4Keys?: string[];
  deduped?: boolean;
}

export async function rerunFinancial(
  sourceRunId: string,
  payload: RerunFinancialPayload = {},
): Promise<RerunFinancialData> {
  return onBoardingApi
    .post(`data-pipeline/financial/${sourceRunId}/rerun`, payload)
    .then((res) => res.data.data);
}

export interface PdfAttachment {
  name: string;
  mimeType: string;
  data: string; // base64-encoded file content
}

export interface ExtractAttachmentsResponse {
  attachments: PdfAttachment[];
}

export async function extractPdfAttachments(
  key: string,
): Promise<ExtractAttachmentsResponse> {
  return onBoardingApi
    .post("data-pipeline/attachments", { key })
    .then((res) => res.data.data);
}

export async function publishPas3(cins: string[]): Promise<void> {
  return onBoardingApi
    .post(`data-pipeline/publish-pas3`, { cins })
    .then((res) => res.data.data);
}

export type FinancialType = "STANDALONE" | "CONSOLIDATED";

export interface UploadFinancialToCaptablePayload {
  cins: string[];
  financialType: FinancialType;
}

export interface UploadFinancialToCaptableData {
  enqueued: number;
}

export async function uploadFinancialToCaptable(
  payload: UploadFinancialToCaptablePayload,
): Promise<UploadFinancialToCaptableData> {
  return onBoardingApi
    .post(`data-pipeline/financial/upload-to-captable`, payload)
    .then((res) => res.data.data);
}

export async function getMcaFileSignedUrl(key: string): Promise<string> {
  return onBoardingApi
    .get(`stage3/mcaSignedUrl?url=${encodeURIComponent(key)}`)
    .then((res) => res.data.data);
}

export interface UploadStage4ExcelPayload {
  cin: string;
  file: File;
}

export interface UploadStage4ExcelData {
  cin: string;
  s3Key: string;
  fileUrl: string;
  urlExpiry: string;
  fileSize: number;
  filename: string;
}

export async function uploadStage4Excel(
  payload: UploadStage4ExcelPayload,
): Promise<UploadStage4ExcelData> {
  const formData = new FormData();
  formData.append("cin", payload.cin);
  formData.append("file", payload.file);
  return onBoardingApi
    .post(`data-pipeline/stage4/excel`, formData, {
      headers: { "Content-Type": "multipart/form-data" },
    })
    .then((res) => res.data.data);
}

// Bulk-regenerate the Stage-4 onboarding workbook for many CINs from each
// CIN's latest completed Stage-3 (captable) run — for old runs that predate
// auto-fill and never got a Stage-4 workbook. Runs as an in-memory async job
// on the backend (max MAX_BATCH_CINS per request); poll getStage4BackfillStatus
// for progress/results.
export interface EnqueueStage4BackfillPayload {
  cins: string[];
}

export interface EnqueueStage4BackfillData {
  jobId: string;
  total: number;
  status: "RUNNING" | "COMPLETED";
}

export async function enqueueStage4Backfill(
  payload: EnqueueStage4BackfillPayload,
): Promise<EnqueueStage4BackfillData> {
  return onBoardingApi
    .post(`data-pipeline/captable/stage4-backfill`, payload)
    .then((res) => res.data.data);
}

export interface Stage4BackfillItemResult {
  cin: string;
  ok: boolean;
  // True when this CIN already had a Stage-4 workbook and nothing was regenerated.
  skipped?: boolean;
  runId?: string;
  stage4Key?: string;
  stage4Url?: string;
  error?: string;
}

export interface Stage4BackfillStatusData {
  jobId: string;
  status: "RUNNING" | "COMPLETED";
  total: number;
  processed: number;
  results: Stage4BackfillItemResult[];
}

export async function getStage4BackfillStatus(
  jobId: string,
): Promise<Stage4BackfillStatusData> {
  return onBoardingApi
    .get(`data-pipeline/captable/stage4-backfill/${jobId}`)
    .then((res) => res.data.data);
}
